import { createSelector } from 'reselect';

import {
  AbsoluteLine,
  CalculatedLine,
  Component,
  Direction2D as Direction2DInterface,
  DivisionLine,
  LineLocation,
  MemberLine,
  ParallelLine,
  PerpendicularLine,
  PointSlopeLine,
  PointToPointLine,
  RelativeBearingGeometry,
  RelativeContinuousBearingGeometry,
  RelativeGeometry,
  RelativeMemberGeometry,
} from '@paragon/contract-library';
import {
  angleGreaterThanOrEqual,
  convertRadiansToDegrees,
  Direction2D,
  Line2D,
  LineSegment2D,
  rightAngle,
  straightAngle,
} from '@paragon/geometry-library';
import { assertExhaustiveSwitch, notNullish } from '@paragon/utilities';

import { getAllRelativeGeometries } from './utilities';

export function findAndCalculateLine(lineGuid: string | null, component: Component) {
  if (lineGuid == null) return null;
  const line = findLine(lineGuid, component);
  if (!line) return null;
  return calculateLine(line, component);
}

export function findLine(lineGuid: string | null, component: Component) {
  if (lineGuid == null) return null;

  const line = component.referenceLines?.find((referenceLine) => referenceLine.guid === lineGuid) ?? null;

  if (line == null) console.error('Could not find reference line');
  return line;
}

export function calculateLine(line: CalculatedLine, component: Component): Line2D | null {
  const lineIsCircularlyDependent = getIsLineCircularlyDependent(component)(line.guid);
  if (lineIsCircularlyDependent) return null;

  switch (line.lineType) {
    case 'Absolute':
      return calculateAbsoluteLine(line);
    case 'Parallel':
      return calculateParallelLine(line, component);
    case 'PointSlope':
      return calculatePointSlopeLine(line, component);
    case 'PointToPoint':
      return calculatePointToPointLine(line, component);
    case 'Perpendicular':
      return calculatePerpendicularLine(line, component);
    case 'Division':
      return calculateDivisionLine(line, component);
    case 'Member':
      return calculateMemberLine(line, component);
    default:
      return null;
  }
}

const calculateAbsoluteLine = createSelector(
  (line: AbsoluteLine) => line,
  (absoluteLine) => Line2D.fromObject(absoluteLine.line),
);

const calculateParallelLine = createSelector(
  (line: ParallelLine) => line,
  (line: ParallelLine, component: Component) => findAndCalculateLine(line.lineGuid, component),
  (parallelLine, line) => {
    if (!line) return null;
    const basePoint = line.basePoint.translate(line.direction.rotateQuarterTurn().toVector(parallelLine.offset));
    return new Line2D(basePoint, line.direction);
  },
);

const calculatePointSlopeLine = createSelector(
  (line: PointSlopeLine) => line,
  (line: PointSlopeLine, component: Component) => findAndCalculateLine(line.line1Guid, component),
  (line: PointSlopeLine, component: Component) => findAndCalculateLine(line.line2Guid, component),
  (pointSlopeLine, line1, line2) => {
    if (!line1 || !line2) return null;
    const point = line1.intersectWithLine(line2);
    if (!point) return null;
    const basePoint = point.translate(Direction2D.up.toVector(pointSlopeLine.offset));
    return new Line2D(basePoint, Direction2D.fromObject(pointSlopeLine.direction));
  },
);

const calculatePointToPointLine = createSelector(
  (line: PointToPointLine) => line,
  (line: PointToPointLine, component: Component) => findAndCalculateLine(line.basePointLine1Guid, component),
  (line: PointToPointLine, component: Component) => findAndCalculateLine(line.basePointLine2Guid, component),
  (line: PointToPointLine, component: Component) => findAndCalculateLine(line.endPointLine1Guid, component),
  (line: PointToPointLine, component: Component) => findAndCalculateLine(line.endPointLine2Guid, component),
  (pointToPointLine, basePointLine1, basePointLine2, endPointLine1, endPointLine2) => {
    if (!basePointLine1 || !basePointLine2 || !endPointLine1 || !endPointLine2) return null;
    const basePoint = basePointLine1.intersectWithLine(basePointLine2);
    const endPoint = endPointLine1.intersectWithLine(endPointLine2);
    if (!basePoint || !endPoint) return null;

    // Caveat: Reversing this rotation would break offsets of lines based on this one.
    const rotation = -Math.asin(pointToPointLine.offset / basePoint.distanceTo(endPoint));
    if (!Number.isFinite(rotation)) return null;

    const direction = Direction2D.fromPoints(endPoint, basePoint).rotate(convertRadiansToDegrees(rotation));
    if (!direction.hasDirection()) return null;

    return new Line2D(basePoint, direction);
  },
);

const calculatePerpendicularLine = createSelector(
  (line: PerpendicularLine) => line,
  (line: PerpendicularLine, component: Component) => findAndCalculateLine(line.basePointLine1Guid, component),
  (line: PerpendicularLine, component: Component) => findAndCalculateLine(line.basePointLine2Guid, component),
  (line: PerpendicularLine, component: Component) => findAndCalculateLine(line.lineGuid, component),
  (perpendicularLine, basePointLine1, basePointLine2, line) => {
    if (!basePointLine1 || !basePointLine2 || !line) return null;
    const basePoint = basePointLine1.intersectWithLine(basePointLine2);
    if (!basePoint) return null;
    let perpendicularAngle = line.direction.angle + rightAngle;
    while (angleGreaterThanOrEqual(perpendicularAngle, rightAngle)) {
      perpendicularAngle -= straightAngle;
    }
    const perpendicularOffsetAngle = perpendicularAngle + rightAngle;
    const newBasePoint = basePoint.translate(
      Direction2D.fromAngle(perpendicularOffsetAngle).toVector(perpendicularLine.offset),
    );
    return new Line2D(newBasePoint, Direction2D.fromAngle(perpendicularAngle));
  },
);

const calculateDivisionLine = createSelector(
  (line: DivisionLine) => line,
  (line: DivisionLine, component: Component) => findAndCalculateLine(line.line1Guid, component),
  (line: DivisionLine, component: Component) => findAndCalculateLine(line.line2Guid, component),
  (divisionLine, line1, line2) => {
    if (!line1 || !line2) return null;
    const perpendicularLine = new Line2D(line1.basePoint, line1.direction.rotateQuarterTurn());
    const point1 = perpendicularLine.intersectWithLine(line1);
    const point2 = perpendicularLine.intersectWithLine(line2);
    if (!point1 || !point2) return null;
    if (point1.equals(point2)) return line1;
    const perpendicularSegment = new LineSegment2D(point1, point2);
    const newBasePoint = point1.translate(
      perpendicularSegment.direction.toVector(perpendicularSegment.length * divisionLine.divisionFactor),
    );
    return new Line2D(newBasePoint, line1.direction);
  },
);

function calculateMemberLine(memberLine: MemberLine, component: Component) {
  const memberGeometry = findRelativeGeometry(memberLine.memberGeometryGuid, component);
  return memberGeometry.geometryType === 'Member' || memberGeometry.bearingGeometryType === 'Bearing'
    ? calculateMemberLineForNormalMemberOrBearingGeometry(memberLine, memberGeometry, component)
    : calculateMemberLineForContinuousBearingGeometry(memberLine, memberGeometry, component);
}

const calculateMemberLineForNormalMemberOrBearingGeometry = createSelector(
  (line: MemberLine) => line,
  (line: MemberLine, memberGeometry: RelativeMemberGeometry | RelativeBearingGeometry) => memberGeometry,
  (line: MemberLine, memberGeometry: RelativeMemberGeometry | RelativeBearingGeometry, component: Component) =>
    findAndCalculateLine(memberGeometry.lineGuid, component),
  (memberLine, memberGeometry, line) =>
    calculateLineLocationOfMemberOrNonContinuousBearingGeometry(memberGeometry, line, memberLine.lineLocation),
);

const calculateMemberLineForContinuousBearingGeometry = createSelector(
  (line: MemberLine) => line,
  (line: MemberLine, bearingGeometry: RelativeContinuousBearingGeometry) => bearingGeometry,
  (line: MemberLine, bearingGeometry: RelativeContinuousBearingGeometry, component: Component) =>
    findAndCalculateLine(bearingGeometry.bearingLineGuid, component),
  (line: MemberLine, bearingGeometry: RelativeContinuousBearingGeometry, component: Component) =>
    findAndCalculateLine(bearingGeometry.leftSideLineGuid, component),
  (line: MemberLine, bearingGeometry: RelativeContinuousBearingGeometry, component: Component) =>
    findAndCalculateLine(bearingGeometry.rightSideLineGuid, component),
  (memberLine, bearingGeometry, bearingLine, leftSideLine, rightSideLine) => {
    if (!bearingLine || !leftSideLine || !rightSideLine) return null;
    const backTopLeftBearingPoint = bearingLine.intersectWithLine(leftSideLine);
    const backTopRightBearingPoint = bearingLine.intersectWithLine(rightSideLine);
    if (!backTopLeftBearingPoint || !backTopRightBearingPoint) return null;
    switch (memberLine.lineLocation) {
      case 'Port':
        return leftSideLine;
      case 'Starboard':
        return rightSideLine;
      case 'Center': {
        const leftPoint = bearingLine.intersectWithLine(leftSideLine);
        const rightPoint = bearingLine.intersectWithLine(rightSideLine);
        if (!leftPoint || !rightPoint) return null;
        if (leftPoint.equals(rightPoint)) return leftSideLine;
        const perpendicularSegment = new LineSegment2D(leftPoint, rightPoint);
        const newBasePoint = leftPoint.translate(
          perpendicularSegment.direction.toVector(perpendicularSegment.length / 2),
        );
        return new Line2D(newBasePoint, leftSideLine.direction);
      }
    }
  },
);

export function calculateLineLocationOfMemberOrNonContinuousBearingGeometry(
  relativeGeometry: RelativeMemberGeometry | RelativeBearingGeometry,
  mainLine: Line2D | null,
  desiredLineLocation: LineLocation,
) {
  if (!mainLine) return null;
  switch (relativeGeometry.lineLocation) {
    case 'Center':
      switch (desiredLineLocation) {
        case 'Center':
          return mainLine;
        case 'Port': {
          const basePoint = mainLine.basePoint.translate(
            mainLine.direction.rotateQuarterTurn().toVector(relativeGeometry.width / 2),
          );
          return new Line2D(basePoint, mainLine.direction);
        }
        case 'Starboard': {
          const basePoint = mainLine.basePoint.translate(
            mainLine.direction.rotateQuarterTurn().toVector(-relativeGeometry.width / 2),
          );
          return new Line2D(basePoint, mainLine.direction);
        }
        default:
          assertExhaustiveSwitch(desiredLineLocation, 'Unknown line location.');
      }
    case 'Port':
      switch (desiredLineLocation) {
        case 'Center': {
          const basePoint = mainLine.basePoint.translate(
            mainLine.direction.rotateQuarterTurn().toVector(-relativeGeometry.width / 2),
          );
          return new Line2D(basePoint, mainLine.direction);
        }
        case 'Port':
          return mainLine;
        case 'Starboard': {
          const basePoint = mainLine.basePoint.translate(
            mainLine.direction.rotateQuarterTurn().toVector(-relativeGeometry.width),
          );
          return new Line2D(basePoint, mainLine.direction);
        }
        default:
          assertExhaustiveSwitch(desiredLineLocation, 'Unknown line location.');
      }
    case 'Starboard':
      switch (desiredLineLocation) {
        case 'Center': {
          const basePoint = mainLine.basePoint.translate(
            mainLine.direction.rotateQuarterTurn().toVector(relativeGeometry.width / 2),
          );
          return new Line2D(basePoint, mainLine.direction);
        }
        case 'Port': {
          const basePoint = mainLine.basePoint.translate(
            mainLine.direction.rotateQuarterTurn().toVector(relativeGeometry.width),
          );
          return new Line2D(basePoint, mainLine.direction);
        }
        case 'Starboard':
          return mainLine;
        default:
          assertExhaustiveSwitch(desiredLineLocation, 'Unknown line location.');
      }
    default:
      assertExhaustiveSwitch(relativeGeometry.lineLocation, 'Unknown line location.');
  }
}

export type ExplicitCalculatedLine = Exclude<CalculatedLine, MemberLine>;

export function isExplicitCalculatedLine(line: CalculatedLine): line is ExplicitCalculatedLine {
  return line.lineType !== 'Member';
}

export function getParentGuids(line: ExplicitCalculatedLine) {
  switch (line.lineType) {
    case 'Absolute':
      return [];
    case 'Parallel':
      return [line.lineGuid];
    case 'PointSlope':
      return [line.line1Guid, line.line2Guid];
    case 'PointToPoint':
      return [line.basePointLine1Guid, line.basePointLine2Guid, line.endPointLine1Guid, line.endPointLine2Guid];
    case 'Perpendicular':
      return [line.basePointLine1Guid, line.basePointLine2Guid, line.lineGuid];
    case 'Division':
      return [line.line1Guid, line.line2Guid];
    default:
      return assertExhaustiveSwitch(line);
  }
}

function getRelativeGeometryParentGuids(relativeGeometry: RelativeGeometry) {
  return relativeGeometry.geometryType === 'Member'
    ? [relativeGeometry.lineGuid]
    : relativeGeometry.bearingGeometryType === 'Bearing'
      ? [relativeGeometry.lineGuid, relativeGeometry.bearingLineGuid]
      : relativeGeometry.bearingGeometryType === 'ContinuousBearing'
        ? [relativeGeometry.leftSideLineGuid, relativeGeometry.rightSideLineGuid, relativeGeometry.bearingLineGuid]
        : assertExhaustiveSwitch(relativeGeometry);
}

function findRelativeGeometry(geometryGuid: string, component: Component) {
  const memberGeometry = getAllRelativeGeometries(component).find(({ guid }) => guid === geometryGuid);
  if (!memberGeometry) throw new Error('Could not find member geometry.');
  return memberGeometry;
}

export const calculateLineGeneration = createSelector(
  (line: CalculatedLine) => line,
  (line: CalculatedLine, component: Component) => component,
  (line, component): number => {
    const parentGuids = isExplicitCalculatedLine(line)
      ? getParentGuids(line)
      : getRelativeGeometryParentGuids(findRelativeGeometry(line.memberGeometryGuid, component));
    const parentGenerations = parentGuids
      .map((guid) => findLine(guid, component))
      .filter(notNullish)
      .map((line) => calculateLineGeneration(line, component));
    return parentGenerations.length ? Math.max(...parentGenerations) + 1 : 0;
  },
);

const getIsLineCircularlyDependent = createSelector(
  (component: Component) => component,
  (component) => {
    const { referenceLines } = component;
    if (!referenceLines) return () => false;
    let nonFullyDefinedLines = referenceLines.map((line) => ({
      guid: line.guid,
      parentGuids: (isExplicitCalculatedLine(line)
        ? getParentGuids(line)
        : getRelativeGeometryParentGuids(findRelativeGeometry(line.memberGeometryGuid, component))
      ).filter(notNullish),
    }));
    let previousCount = 0;
    while (previousCount !== nonFullyDefinedLines.length) {
      previousCount = nonFullyDefinedLines.length;
      nonFullyDefinedLines = nonFullyDefinedLines.filter(({ parentGuids }) =>
        parentGuids.some((parentGuid) => nonFullyDefinedLines.find(({ guid }) => guid === parentGuid)),
      );
    }

    const circularlyDependentLineGuids = new Set(nonFullyDefinedLines.map(({ guid }) => guid));
    return circularlyDependentLineGuids.has.bind(circularlyDependentLineGuids);
  },
);

const getPrimaryDirection = ({ x, y }: Direction2DInterface) =>
  Math.abs(x) > Math.abs(y) ? (x > 0 ? 'Right' : 'Left') : y > 0 ? 'Up' : 'Down';

export const getDirectionalBaseAndEndTerms = (direction: Direction2DInterface | null) => {
  const primaryDirection = direction && getPrimaryDirection(direction);
  return primaryDirection === 'Right'
    ? ({ baseTerm: 'Left', endTerm: 'Right' } as const)
    : primaryDirection === 'Left'
      ? ({ baseTerm: 'Right', endTerm: 'Left' } as const)
      : primaryDirection === 'Up'
        ? ({ baseTerm: 'Bottom', endTerm: 'Top' } as const)
        : primaryDirection === 'Down'
          ? ({ baseTerm: 'Top', endTerm: 'Bottom' } as const)
          : primaryDirection === null
            ? ({ baseTerm: 'Base', endTerm: 'End' } as const)
            : assertExhaustiveSwitch(primaryDirection);
};

export const getDirectionalLineLocationTerms = (direction: Direction2DInterface | null) => {
  const primaryDirection = direction && getPrimaryDirection(direction);
  const terms =
    primaryDirection === 'Right'
      ? ({ Port: 'Below', Starboard: 'Above' } as const)
      : primaryDirection === 'Left'
        ? ({ Port: 'Above', Starboard: 'Below' } as const)
        : primaryDirection === 'Up'
          ? ({ Port: 'Right Of', Starboard: 'Left Of' } as const)
          : primaryDirection === 'Down'
            ? ({ Port: 'Left Of', Starboard: 'Right Of' } as const)
            : primaryDirection === null
              ? ({ Port: 'Port Side Of', Starboard: 'Starboard Side Of' } as const)
              : assertExhaustiveSwitch(primaryDirection);
  return {
    terms: { ...terms, Center: 'Centered On' as const },
    swapPortAndStarboardOrdering: primaryDirection === 'Right' || primaryDirection === 'Up',
  };
};
